import { ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Chip,
  LinearProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import axios from "../../../api/axios";
import CustomModal from "../../ui/modal/modal.component";
import EntityReferenceLink from "../../ui/entityReference/entityReferenceLink.component";
import { useHasAnyPermission, useHasPermission } from "../../../contexts/auth";
import { SETTLEMENTS_PROFITABILITY_BREAKDOWN_ENDPOINT } from "../../../consts";
import { ProfitabilityBreakdown } from "../../../types/profitability";
import {
  formatCounterValue,
  formatDateTimeInline,
  formatNumber,
  formatQuantity,
} from "../../../utils";
import { buildOrderedPartReferenceId } from "../../../utils/entityRoutes";

type ProfitabilityBreakdownModalProps = {
  opened: boolean;
  setOpened: (value: boolean) => void;
  /** Every settlement the clicked figure sums - one for a settlement, all of
   * them for a contract summary or a grouped profitability row. */
  settlementIds: number[];
  /** Names what the figure belongs to (equipment, contract, client). */
  subtitle?: string | null;
};

type Column = { label: string; align?: "right" };

/**
 * A cost figure that opens its breakdown. Styled like the entity links next to
 * it (contract, client, equipment) - blue, underlined on hover - so it reads as
 * clickable the same way they do.
 */
export const CostBreakdownLink = ({
  value,
  onOpen,
}: {
  value: number | string | null | undefined;
  onOpen: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <Link
      component="button"
      type="button"
      underline="hover"
      title={t("Cost breakdown")}
      onClick={(event) => {
        event.stopPropagation();
        onOpen();
      }}
      // Same size as the plain figure it replaced - whatever surrounds it.
      sx={{ fontSize: "inherit", lineHeight: "inherit", textAlign: "left", cursor: "pointer" }}
    >
      {formatNumber(value)}
    </Link>
  );
};

/**
 * What a cost and its additional costs are made of: the pages printed, the
 * jobs finished in the period and the parts used on them. Opened from both
 * figures alike, in the sale summary and on the Profitability tab.
 */
export const ProfitabilityBreakdownModal = ({
  opened,
  setOpened,
  settlementIds,
  subtitle,
}: ProfitabilityBreakdownModalProps) => {
  const { t } = useTranslation();
  const canViewEquipment = useHasPermission("settlement.view_equipment");
  const canViewJobs = useHasAnyPermission(["service.view_job", "service.view_own_job"]);
  const canViewOrderedParts = useHasPermission("service.view_orderedpart");
  const [breakdown, setBreakdown] = useState<ProfitabilityBreakdown | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  const settlementIdsParam = settlementIds.join(",");

  useEffect(() => {
    if (!opened || !settlementIdsParam) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    setBreakdown(null);
    axios
      .get(SETTLEMENTS_PROFITABILITY_BREAKDOWN_ENDPOINT, {
        params: { settlement_ids: settlementIdsParam },
      })
      .then((response) => {
        if (!cancelled) setBreakdown(response.data as ProfitabilityBreakdown);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [opened, settlementIdsParam]);

  // A row that isn't a line: a label spanning every column but the last, and
  // an amount in the last one - for totals and the "without details" row.
  const renderSpanningRow = (
    key: string,
    columnCount: number,
    label: ReactNode,
    value: string,
    isTotal = false
  ) => (
    <TableRow
      key={key}
      sx={
        isTotal
          ? { "& > td": { fontWeight: 700, borderTop: 2, borderTopColor: "divider", borderBottom: 0 } }
          : { "& > td": { color: "text.secondary", fontStyle: "italic" } }
      }
    >
      <TableCell colSpan={columnCount - 1}>{label}</TableCell>
      <TableCell align="right">{formatNumber(value)}</TableCell>
    </TableRow>
  );

  const renderSection = ({
    title,
    columns,
    rows,
    emptyText,
    extraRows = [],
    totalLabel,
    total,
  }: {
    title: string;
    columns: Column[];
    rows: ReactNode[][];
    emptyText: string;
    extraRows?: ReactNode[];
    totalLabel: string;
    total: string;
  }) => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Typography variant="subtitle2">{title}</Typography>
      <TableContainer sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column.label} align={column.align}>
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && extraRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} sx={{ color: "text.secondary" }}>
                  {emptyText}
                </TableCell>
              </TableRow>
            ) : null}
            {rows.map((cells, rowIndex) => (
              // Lines are listed per settlement and may legitimately repeat
              // (a job costed on two settlements), so the index is the key.
              <TableRow key={rowIndex}>
                {cells.map((cell, cellIndex) => (
                  <TableCell key={cellIndex} align={columns[cellIndex].align}>
                    {cell}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {extraRows}
            {renderSpanningRow("total", columns.length, totalLabel, total, true)}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  const renderContent = () => {
    if (loading) {
      return <LinearProgress />;
    }
    if (failed) {
      return <Alert severity="error">{t("Failed to load the cost breakdown")}</Alert>;
    }
    if (!breakdown) {
      return null;
    }

    const hasSettlementsWithoutDetails = breakdown.without_details_count > 0;
    const withoutDetailsLabel = t("Settlements without saved details ({{count}})", {
      count: breakdown.without_details_count,
    });

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
        {hasSettlementsWithoutDetails ? (
          <Alert severity="info">
            {t(
              "{{count}} settlement(s) were confirmed before their cost details were saved. Their costs are included in the totals but not listed - recalculate profitability for their month to list them.",
              { count: breakdown.without_details_count }
            )}
          </Alert>
        ) : null}

        {renderSection({
          title: t("Printed pages"),
          columns: [
            { label: t("Equipment") },
            { label: t("Counter Kind") },
            { label: t("Pages"), align: "right" },
            { label: t("Cost per page"), align: "right" },
            { label: t("Amount"), align: "right" },
          ],
          rows: breakdown.usage_lines.map((line) => [
            <EntityReferenceLink
              entityType="equipment"
              entityId={line.equipment_id}
              label={line.equipment_name}
              canPreview={canViewEquipment}
            />,
            line.counter_kind,
            formatCounterValue(line.pages),
            formatNumber(line.cost_per_page),
            formatNumber(line.amount),
          ]),
          emptyText: t("No pages were printed in this period."),
          totalLabel: t("Total"),
          total: breakdown.usage_cost,
        })}

        {renderSection({
          title: t("Jobs"),
          columns: [
            { label: t("Job") },
            { label: t("Finished at") },
            { label: t("Costed by") },
            { label: t("Cost"), align: "right" },
          ],
          rows: breakdown.jobs.map((job) => [
            <EntityReferenceLink
              entityType="job"
              entityId={job.job_id}
              label={job.job_number}
              canPreview={canViewJobs}
            />,
            formatDateTimeInline(job.finished_at),
            job.uses_mean_job_cost ? (
              <Chip
                size="small"
                variant="outlined"
                label={t("Mean job execution cost")}
                title={t("None of the job's services has a price")}
              />
            ) : (
              t("Services")
            ),
            formatNumber(job.cost),
          ]),
          emptyText: t("No jobs were finished in this period."),
          totalLabel: t("Total"),
          total: breakdown.job_cost,
        })}

        {/* The figure shown as "Cost" on the settlement: the two tables above,
            plus whatever settlements have no details to list. */}
        {renderSection({
          title: t("Cost"),
          columns: [{ label: t("Cost component") }, { label: t("Amount"), align: "right" }],
          rows: [
            [t("Printed pages"), formatNumber(breakdown.usage_cost)],
            [t("Jobs"), formatNumber(breakdown.job_cost)],
          ],
          emptyText: "",
          extraRows: hasSettlementsWithoutDetails
            ? [
                renderSpanningRow(
                  "without-details",
                  2,
                  withoutDetailsLabel,
                  breakdown.without_details_cost
                ),
              ]
            : [],
          totalLabel: t("Cost"),
          total: breakdown.cost,
        })}

        {renderSection({
          title: t("Additional costs"),
          columns: [
            { label: t("Job") },
            { label: t("Part") },
            { label: t("Quantity"), align: "right" },
            { label: t("Net price"), align: "right" },
            { label: t("Amount"), align: "right" },
          ],
          rows: breakdown.used_parts.map((part) => [
            <EntityReferenceLink
              entityType="job"
              entityId={part.job_id}
              label={part.job_number}
              canPreview={canViewJobs}
            />,
            <EntityReferenceLink
              entityType="orderedPart"
              entityId={buildOrderedPartReferenceId(part.part_id, part.ordered_part_id)}
              label={part.part_name}
              canPreview={canViewOrderedParts}
            />,
            formatQuantity(part.quantity),
            formatNumber(part.price_net),
            formatNumber(part.amount),
          ]),
          emptyText: t("No parts were used on these jobs."),
          extraRows:
            hasSettlementsWithoutDetails &&
            Number(breakdown.without_details_additional_costs) !== 0
              ? [
                  renderSpanningRow(
                    "without-details",
                    5,
                    withoutDetailsLabel,
                    breakdown.without_details_additional_costs
                  ),
                ]
              : [],
          totalLabel: t("Total"),
          total: breakdown.additional_costs,
        })}
      </Box>
    );
  };

  return (
    <CustomModal
      opened={opened}
      setOpened={setOpened}
      title={subtitle ? `${t("Cost breakdown")} - ${subtitle}` : t("Cost breakdown")}
      width="min(1000px, 95vw)"
      content={<Box sx={{ px: 2, pb: 2 }}>{renderContent()}</Box>}
    />
  );
};

export default ProfitabilityBreakdownModal;
